import type React from 'react';

import { Checkbox } from '@vlabs/uikit/components/controls/checkbox';
import type { WithDataTestid } from '@vlabs/uikit/types';
import type { PathValue } from 'react-hook-form';

import type { EditPermissionsFormProps, FilterPropsByPermission } from '../types';
import st from './Cell.module.sass';
import { useEditPermissionsFormContext } from '../useEditPermissionsFormContext';

type Permission = 'read' | 'create' | 'update' | 'delete';

interface CellProps<T extends Permission> extends WithDataTestid {
  fieldName: keyof FilterPropsByPermission<T>;
  permission: T;
  disabled?: boolean;
}

const toggleArrayElement = (arr: string[], val: string): string[] => (
  arr.includes(val)
    ? arr.filter((el) => el !== val)
    : [...arr, val]
);

function Cell<T extends Permission>({
  fieldName,
  permission,
  disabled = false,
  'data-testid': dataTestId = undefined,
}: CellProps<T>): React.JSX.Element {
  const { watch, setValue } = useEditPermissionsFormContext();
  const permissionList = (watch(fieldName) ?? []) as (FilterPropsByPermission<T>[keyof FilterPropsByPermission<T>]);

  return (
    <div className={st.Cell}>
      <Checkbox
        checked={(permissionList as string[] | undefined)?.includes(permission)}
        data-testid={dataTestId}
        disabled={disabled}
        onClick={() => {
          // eslint-disable-next-line @stylistic/max-len
          const next = toggleArrayElement(permissionList as string[], permission) as FilterPropsByPermission<T>[typeof fieldName];
          setValue(
            fieldName,
            next as PathValue<EditPermissionsFormProps, keyof FilterPropsByPermission<T>>,
          );
        }}
      />
    </div>
  );
}

export { Cell };
